import React, { useState, useEffect } from "react";
import {
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  IconButton,
  Skeleton,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";
import { isSubscriptionBlockedError, checkSubscriptionActive } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";
import { toast } from "react-hot-toast";
import { Check } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRolesWithUsage,
  fetchTeamMembers,
} from "../../../redux/Slice/Settings/settingsSlice";
import { updateTeamMemberById } from "../../../redux/Slice/Settings/updatefunctionsSlice";

const ChangeRole = ({ currentRole, memberId, onClose, onChange }) => {
  const dispatch = useDispatch();
  const { rolesWithUsage, loading: rolesLoading, error } = useSelector(
    (state) => state.settings
  );
  const { loading: updateLoading } = useSelector(
    (state) => state.updateFunctions
  );
  const [newRole, setNewRole] = useState(currentRole);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
      dispatch(fetchRolesWithUsage());
  }, [dispatch]);

  const handleChangeRole = (roleName) => {
    setNewRole(roleName);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleConfirmChange = async () => {
    if (newRole !== currentRole) {
      const { blocked, message } = await checkSubscriptionActive(getAuthToken);
      if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
      dispatch(
        updateTeamMemberById({
          id: memberId,
          teamMemberData: { roleName: newRole },
        })
      )
        .unwrap()
        .then(() => {
          setSnackbar({
            open: true,
            message: "Role updated successfully!",
            severity: "success",
          });
          setTimeout(() => {
            onClose(); // Close the modal after a slight delay
          }, 500); // Allow Snackbar to remain visible
          onChange(newRole); // Update the parent component (TeamMembers)
          dispatch(fetchTeamMembers()); // Fetch updated team members
        })
        .catch((error) => {
          if (isSubscriptionBlockedError(error)) return;
          setSnackbar({
            open: true,
            message:
              error.message || "Failed to update role. Please try again.",
            severity: "error",
          });
        });
    } else {
      onClose();
    }
  };

  return (
    <>
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "24px",
          padding: "14px",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Typography variant="h6" component="span">Change Role</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "24px",
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {rolesLoading ? (
            <Skeleton variant="rectangular" width="100%" height={56} />
          ) : (
            <List sx={{ padding: 0 }}>
              {rolesWithUsage
                .filter((role) => role.roleName !== "Owner")
                .map((role) => (
                  <ListItem
                    key={role._id}
                    button
                    selected={newRole === role.roleName}
                    onClick={() => handleChangeRole(role.roleName)}
                    sx={{
                      borderRadius: "12px",
                      marginBottom: 1,
                      "&.Mui-selected": {
                        backgroundColor: (theme) => theme.palette.primary.light,
                        "&:hover": {
                          backgroundColor: (theme) => theme.palette.primary.light,
                        },
                      },
                      "&:hover": {
                        backgroundColor: (theme) => theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemIcon>
                      {newRole === role.roleName && (
                        <Check color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            color: (theme) =>
                              newRole === role.roleName
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                            fontWeight: newRole === role.roleName ? "bold" : "normal",
                          }}
                        >
                          {role.roleName}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
        </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          padding: "16px 24px",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          component="button"
          onClick={onClose}
          sx={{
            borderRadius: "20px",
            textTransform: "capitalize",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            color: (theme) => theme.palette.text.primary,
            backgroundColor: "transparent",
            padding: "8px 16px",
            cursor: "pointer",
            "&:hover": {
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
            Cancel
        </Box>
        <Box
          component="button"
          onClick={handleConfirmChange}
          disabled={updateLoading || newRole === currentRole}
          sx={{
            borderRadius: "20px",
            textTransform: "capitalize",
            backgroundColor: (theme) => theme.palette.primary.main,
            color: "white",
            border: "none",
            padding: "8px 16px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            "&:disabled": {
              backgroundColor: (theme) => theme.palette.action.disabled,
              cursor: "not-allowed",
            },
          }}
        >
            {updateLoading ? "Updating..." : "Change"}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangeRole;
